<template>
  <section class="edit-form-section d-flex flex-column p-2 p-lg-0">
    <h2 class="title-2 text-primary-black">
      {{ t('profile.myInfo') }}
    </h2>
    <div>
      <!-- Pass editable to child components -->
      <InfoFields
        :user-info="userInfo"
        :form-values="formValues"
        :updateFN="updateFormValues"
        :invalid-fields="invalidFields"
        :loading="isLoading"
        :editable="editable"
      />
      <ContactFields
        :user-info="userInfo"
        :form-values="formValues"
        :updateFN="updateFormValues"
        :invalid-fields="invalidFields"
        :loading="isLoading"
        :editable="editable"
      />
    </div>
    <div class="button-container d-flex justify-content-md-end mt-4">
      <Button
        @button-click="handleSave"
        class="col-12 col-md-6"
        :disabled="!editable || isLoading || invalidFields.size !== 0"
        :loading="isLoading"
      >
        <template #text>
          {{ t('profile.editInfoForm.save') }}
        </template>
      </Button>
    </div>
    <InputFieldError
      :text="t('errorMessages.updateUserInfo')"
      v-if="updateError"
    />
  </section>
</template>

<script lang="ts" setup>
  // Vue
  import { ComputedRef, computed, defineProps, ref, Ref } from 'vue'

  // Types
  import { UserInfo } from '@/services/AuthService.interface'
  import { UpdateUserInfoFn, ValidationRules,EditFormFields } from './EditInfoForm.types'

  // Composables
  import { useValidation } from '@/composables/useValidation'
  import { useIsPhoneNumberValid } from '@/composables/useIsPhoneNumberValid'

  // Utils
  import { validateName } from '@/utils'

  // Components
  import InfoFields from './InfoFields.vue'
  import ContactFields from './ContactFields.vue'
  import Button from '@/components/UI/Button.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'

  // Config
  import { ModalId } from '@/config/modalIds'

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Store
  import { useStore } from '@/store'
  const store = useStore()

  // Props
  defineProps({
    editable: {
      type: Boolean,
      required: true,
    },
  })

  // Computed
  const {isPhoneNumberValid} = useIsPhoneNumberValid()


  // Store data
  const userInfo: ComputedRef<UserInfo> = computed(() => {
    const user = store.state.auth?.user
    if (!user) throw new Error('user must be defined in EditInfoForm')
    return user
  })

  const isLoading: ComputedRef<boolean> = computed(() => {
    const isLoadingGetter = store.getters['auth/isLoading']
    return isLoadingGetter('updateUserInfo')
  })

  const updateError: ComputedRef<string | null> = computed(() => {
    const errorGetter = store.getters['auth/getError']
    return errorGetter('updateUserInfo')
  })

  // Refs
  // Edit form values
  const formValues: Ref<Partial<UserInfo>> = ref({
    first_name: userInfo.value.first_name,
    last_name: userInfo.value.last_name,
    address: userInfo.value.address,
    city: userInfo.value.city,
    country: userInfo.value.country,
    phone_code: userInfo.value.phone_code,
    postal_code: userInfo.value.postal_code,
    phone_number: userInfo.value.phone_number.replace(
      userInfo.value.phone_code,
      ''
    ), // remove the phone code from the value
  })

  // Map of invalid fields
  const validationRules: ValidationRules = {
    first_name: (value: string) => validateName(value),
    last_name: (value: string) => validateName(value),
    address: (value: string) => validateName(value),
    city: (value: string) => validateName(value),
    country: (value: string) => validateName(value),
    phone_code: (value: string) => validateName(value),
    postal_code: (value: string) => validateName(value),
    phone_number: (value: string, payload?: EditFormFields) => {
        if (!payload || !payload.phone_code) return false;
        return isPhoneNumberValid(`${payload.phone_code}${value}`);
    },    
  }

  const { invalidFields } = useValidation<Partial<UserInfo>>(
    formValues,
    validationRules
  )

  // Methods

  // Update function to pass to subforms
  const updateFormValues: UpdateUserInfoFn = (key, value) => {
    formValues.value[key] = value
  }

  const handleSave = async (): Promise<void> => {
    try {
      // Merge the updated fields
      const updates: Partial<UserInfo> = {
        ...formValues.value,
      }
      await store.dispatch('auth/updateUserInfo', updates)

      store.commit('modal/close', ModalId.EDIT_INFO_PANEL)
    } catch (err) {
      console.error(err)
    }
  }
</script>

<style scoped>
  .edit-form-section {
    margin-bottom: 20em;
  }
</style>
